// context/AuthContext.tsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import type { User } from "../shared/types/auth";
import type { ReactNode } from "react";
import { authService } from "../shared/services/apis";

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Initialize auth state on mount
  useEffect(() => {
    console.log("Initializing auth context...");
    initializeAuth();
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      setLoading(true);

      // Check if we have a valid token
      const token = authService.getAccessToken();
      if (!token || !authService.isAuthenticated()) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      // Get cached user data first for immediate display
      const cachedUser = authService.getCurrentUser();

      if (cachedUser) {
        setUser(cachedUser);
        setIsAuthenticated(true);
        setLoading(false); // Set loading to false here to prevent redirect to login

        // Try to refresh user data in background
        try {
          const freshUser = await authService.getProfile();
          setUser(freshUser);
        } catch (error: any) {
          // Only logout on auth errors
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            console.warn("Token is invalid, logging out");
            await logout();
          } else {
            // For network/server errors, keep using cached data
            console.warn(
              "Failed to refresh user profile, using cached data:",
              error
            );
          }
        }
      } else {
        // No cached user but have token - try to get profile
        try {
          const freshUser = await authService.getProfile();
          setUser(freshUser);
          setIsAuthenticated(true);
        } catch (error: any) {
          console.warn("Failed to get user profile:", error);
          // Only clear auth on 401/403 errors
          if (error.response?.status === 401 || error.response?.status === 403) {
            await logout();
          }
        }
      }
    } catch (error) {
      console.error("Auth initialization failed:", error);
      await logout();
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn("Logout request failed:", error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      return;
    }

    try {
      const freshUser = await authService.getProfile();
      setUser(freshUser);
    } catch (error: any) {
      console.error("Failed to refresh user:", error);
      // Only logout on auth errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        await logout();
      }
    }
  }, [logout]);

  const value: AuthContextType = useMemo(
    () => ({
      isAuthenticated,
      user,
      loading,
      login,
      logout,
      refreshUser,
    }),
    [isAuthenticated, user, loading, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
};
